import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { get as getProjection } from 'ol/proj';
import OLMap from 'ol/Map';
import { inAndOut, easeOut } from 'ol/easing';
import { defaults as defaultInteractions, PinchRotate } from 'ol/interaction';
import View from 'ol/View';
import { ANIMATION_DURATON } from '../constants';
import { actionMoveEnd, actionMapClick } from '../actions';
import { MapContext } from './MapContext';
export const MapProvider = ({ dispatch, children, ...props }) => {
    const mapRef = useRef(null);
    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.dispose();
                mapRef.current = null;
            }
        };
    }, []);
    if (!mapRef.current && props.zoom) {
        /** nastaveni projekce pro OL */
        if (props.projectionDefinition) {
            proj4.defs(props.projectionDefinition);
            register(proj4);
        }
        const interactions = defaultInteractions({
            onFocusOnly: true,
            pinchRotate: false,
        });
        interactions.push(new PinchRotate({ threshold: 0.6 }));
        mapRef.current = new OLMap({
            controls: [],
            interactions,
            view: new View({
                ...props,
                constrainRotation: false,
                projection: getProjection(props.projection || 'EPSG:4326'),
            }),
        });
        if (process.env.NODE_ENV === 'development') {
            console.debug('new OLMap', mapRef.current, children);
        }
    }
    const olMap = mapRef.current;
    const { zoom, resolution, rotation, center, setExtent } = props;
    // Common map events
    useEffect(() => {
        if (!olMap || !dispatch)
            return;
        const handleMapClick = (event) => {
            const features = [];
            olMap.forEachFeatureAtPixel(event.pixel, function (feature, layer) {
                if (feature && layer) {
                    features.push({ feature, layerId: layer.getProperties().id });
                }
            });
            dispatch(actionMapClick({
                point: event.coordinate,
                resolution: olMap.getView().getResolution() ?? resolution,
                features,
            }));
        };
        olMap.on('singleclick', handleMapClick);
        return () => {
            if (olMap) {
                olMap.un('singleclick', handleMapClick);
            }
        };
    }, [olMap, dispatch, resolution]);
    // Register all needed event handlers for backbressure state updates to redux:
    // https://openlayers.org/en/latest/apidoc/module-ol_Map-Map.html
    // Check if Redux needs update from map
    useEffect(() => {
        if (!dispatch)
            return;
        const handleMoveEnd = (e) => {
            const view = e.map.getView();
            const currentCenter = view.getCenter();
            const currentZoom = view.getZoom();
            const currentResolution = view.getResolution();
            const currentRotation = view.getRotation();
            const changedZoom = currentZoom !== zoom;
            const changedResolution = resolution !== currentResolution;
            const changedRotation = rotation !== currentRotation;
            const centerNeedsUpdate = !center || currentCenter?.some((coord, idx) => coord !== center[idx]);
            if (changedResolution || changedZoom || changedRotation || centerNeedsUpdate) {
                dispatch(actionMoveEnd({
                    zoom: currentZoom,
                    resolution: currentResolution,
                    center: currentCenter,
                    rotation: currentRotation,
                    changedZoom,
                    changedResolution,
                    changedRotation,
                    changedCenter: centerNeedsUpdate,
                }));
            }
        };
        olMap?.on('moveend', handleMoveEnd);
        // dispose all listeners on unmount
        return () => {
            olMap?.un('moveend', handleMoveEnd);
        };
    }, [olMap, dispatch, center, zoom, resolution, rotation]);
    // Check if map needs update Zoom or center from redux
    useEffect(() => {
        if (!olMap)
            return;
        const view = olMap.getView();
        const currentZoom = view.getZoom();
        const currentCenter = view.getCenter();
        const currentRotation = view.getRotation();
        const centerNeedsUpdate = center && center.some((coord, idx) => !(currentCenter && coord === currentCenter[idx]));
        if (rotation !== currentRotation || currentZoom !== zoom || centerNeedsUpdate) {
            let easing = inAndOut;
            if (view.getAnimating()) {
                view.cancelAnimations();
                easing = easeOut;
            }
            view.animate({
                zoom,
                center,
                rotation,
                duration: ANIMATION_DURATON.ZOOM,
                easing,
            });
        }
    }, [olMap, zoom, center, rotation]);
    useEffect(() => {
        if (!olMap || !setExtent)
            return;
        let maxTries = 100; // 100 rendering frames
        const updateExtent = () => {
            if (olMap.getTargetElement()) {
                const view = olMap.getView();
                let easing = inAndOut;
                if (view.getAnimating()) {
                    view.cancelAnimations();
                    easing = easeOut;
                }
                view.fit(setExtent.extent, {
                    duration: ANIMATION_DURATON.ZOOM,
                    easing,
                    padding: setExtent.padding ?? [10, 10, 10, 10],
                    maxZoom: setExtent.maxZoom,
                });
            }
            else if (maxTries--) {
                requestAnimationFrame(updateExtent);
            }
        };
        updateExtent();
    }, [olMap, setExtent]);
    if (!olMap)
        return null;
    return _jsx(MapContext.Provider, { value: olMap, children: children });
};
//# sourceMappingURL=MapProvider.js.map